// General
import "./masquerade-party-view.scss";
import { useState, useEffect } from "react";
// Services
import { useLazyGetDatingMasqueradePartyQuery } from "../../../../../services/data.service";
// Static Data
import routeConst from "../../../../../const/routeConst";
import searchConst from "../../../../../const/searchConst";
import userCardConst from "../../../../../const/userCardConst";
// Redux
import { useSelector, useDispatch } from "react-redux";
import {
  updateMasqueradePartyLists,
  resetMasqueradePartyLists,

  // Pagination Functions
  updateMasqueradePartyPagination,

  // Utility Functions
  updateRestartJourneyCounter,
} from "../../../../../redux/store/searchStore";
// React Infinite Scroller
import InfiniteScroll from "react-infinite-scroller";
// react-simple-pull-to-refresh
import PullToRefresh from "react-simple-pull-to-refresh";
// Material UI
import { Divider, useMediaQuery } from "@mui/material";
// i18next
import { useTranslation } from "react-i18next";
// Custom Hooks
import useCustomNavigate from "../../../../utility/custom-hooks/useCustomNavigate-hook";
import useGetUserProfileStatus from "../../../../utility/custom-hooks/useGetUserProfileStatus-hook";
// Components
import UserCardManager from "../../../../utility/manager/user-card-manager/user-card-manager";
import EmptyList from "../empty-list/empty-list";
import SkeletonUserCardList from "../../../../shared/skeleton-user-card-list/skeleton-user-card-list";
import Spinner from "../../../../shared/elements/spinner/spinner";
import ResultEndCard from "../../../../shared/elements/result-end-card/result-end-card";

const MasqueradePartyView = () => {
  // API variables
  const [
    getDatingMasqueradeParty,
    {
      data: getDatingMasqueradePartyData,
      error: getDatingMasqueradePartyErrorData,
      isFetching: getDatingMasqueradePartyFetching,
      isLoading: getDatingMasqueradePartyLoading,
      isSuccess: getDatingMasqueradePartySuccess,
      isError: getDatingMasqueradePartyError,
    },
  ] = useLazyGetDatingMasqueradePartyQuery();

  // General variables
  const [firstLoad, setFirstLoad] = useState(true);
  const [isApiLoading, setIsApiLoading] = useState(false);

  // Render variables
  let masqueradePartyListView = <div></div>;

  // Redux variables
  const selectedCategoryTab = useSelector(
    (state) => state.search.selectedCategoryTab
  );
  const selectedView = useSelector((state) => state.search.selectedView);
  const masqueradePartyLists = useSelector(
    (state) => state.search.masqueradePartyLists
  );
  const masqueradePartyPagination = useSelector(
    (state) => state.search.masqueradePartyPagination
  );
  const isDaddy = useSelector((state) => state.user.isDaddy);
  const dispatch = useDispatch();

  // MUI variables
  const isMobile = useMediaQuery("(max-width: 720px)");

  const body = {
    size: 16,
    location: {},
    sorting: "masquerade_party",
    filters: {},
  };

  // i18next variables
  const { t } = useTranslation();

  // Custom Hooks Functions
  const onNavigate = useCustomNavigate();
  const getProfileStatus = useGetUserProfileStatus();

  // Lifecycle | Check for update | Dating Masquerade Party API Response
  useEffect(() => {
    if (getDatingMasqueradePartyFetching || getDatingMasqueradePartyLoading) {
    } else if (getDatingMasqueradePartySuccess) {
      dispatch(
        updateMasqueradePartyPagination(
          getDatingMasqueradePartyData?.data?.pagination?.next_cursor
        )
      );

      switch (getDatingMasqueradePartyData?.status) {
        case 0:
        case 1:
        case -1:
          if (getDatingMasqueradePartyData?.data?.results?.length > 0) {
            setFirstLoad(false);

            // Use to block infiniteScrollFunc from running into infinite loop
            setIsApiLoading(false);

            dispatch(
              updateMasqueradePartyLists(
                getDatingMasqueradePartyData?.data?.results
              )
            );
          }
          break;
        default:
          break;
      }
    } else if (getDatingMasqueradePartyError) {
      if (getDatingMasqueradePartyErrorData?.status === 401) {
        onNavigate(routeConst.logout.path);
      }
    }
  }, [
    getDatingMasqueradePartyFetching,
    getDatingMasqueradePartyLoading,
    getDatingMasqueradePartySuccess,
    getDatingMasqueradePartyError,
  ]);

  // Lifecycle | Check for update | Selected Category Tab
  useEffect(() => {
    if (selectedCategoryTab === "masquerade_party") {
      if (masqueradePartyLists.length <= 0) {
        if (masqueradePartyPagination) {
          setFirstLoad(false);
        } else {
          setFirstLoad(true);
        }

        getDatingMasqueradeParty(
          {
            param: masqueradePartyPagination
              ? `?pagination_token=${masqueradePartyPagination}`
              : null,
            body,
          },
          false
        );
      } else {
        setFirstLoad(false);
      }
    }
  }, [selectedCategoryTab]);

  // Utility Functions
  const getUserCardType = (user) => {
    if (user?.type === "gaming") {
      return "gaming";
    } else if (
      user?.user?.live_stream?.co_anchor &&
      user?.user?.live_stream?.live_status === "paused"
    ) {
      return "co_anchor";
    } else {
      return user?.type;
    }
  };

  // Infinite Scroller Functions
  const infiniteScrollFunc = () => {
    if (!isApiLoading && masqueradePartyPagination) {
      setIsApiLoading(true);
      dispatch(updateRestartJourneyCounter());
      getDatingMasqueradeParty(
        {
          param: masqueradePartyPagination
            ? `?pagination_token=${masqueradePartyPagination}`
            : null,
          body,
        },
        false
      );
    }
  };

  // Pull To Refresh Functions
  const pullToRefreshFunc = () => {
    dispatch(resetMasqueradePartyLists());
    dispatch(updateMasqueradePartyPagination(null));
    setFirstLoad(true);

    return new Promise((resolve) => {
      resolve(getDatingMasqueradeParty({ param: null, body }));
    });
  };

  // Render Functions | Dating Masquerade Party List Data
  if (selectedCategoryTab === "masquerade_party") {
    if (masqueradePartyLists?.length > 0) {
      masqueradePartyListView = (
        <div className={`infinite-scroller-container ${selectedView}`}>
          {masqueradePartyLists?.map((user, index) => (
            <div className="list-container" key={index}>
              <UserCardManager
                // Config
                type={getUserCardType(user)}
                // User View
                cardView={selectedView}
                id={user?.user?.id}
                idInt={user?.user?.id_int}
                username={user?.user?.username}
                age={user?.user?.appearance?.age}
                gender={user?.user?.gender}
                location={
                  user?.user?.location?.display_address ||
                  user?.user?.location?.city ||
                  user?.user?.location?.state
                }
                membershipType={user?.user?.membership_type}
                verifiedProfile={user?.user?.is_verified_profile}
                verifiedCaller={user?.user?.verified_caller}
                levellingBadge={user?.user?.levelling_profile?.badge}
                livestreamingAchievementBadge={
                  user?.user?.live_streaming_achievements?.chat_badges_set
                }
                specialBadges={user?.user?.special_badges}
                profilePhoto={user?.user?.profile_photo?.original_photo}
                profileDecorations={user?.user?.profile_decorations}
                publicPhotos={user?.user?.public_photos?.map((photo) => {
                  return {
                    photoUrl: photo?.original_photo,
                  };
                })}
                privatePhotos={user?.user?.private_photos?.map((photo) => {
                  return {
                    photoUrl: photo?.original_photo,
                    thumbnailData: photo?.thumbnail_data,
                    isSensitive: photo?.is_sensitive,
                  };
                })}
                torTags={user?.user?.tor_tags}
                datingStyle={user?.user?.relationship?.dating_style}
                profileAchievementBadge={
                  user?.user?.live_streaming_achievements?.profile_badges
                }
                aboutMe={user?.user?.aboutme}
                lookingFor={user?.user?.interest}
                tagline={user?.user?.tagline}
                annualIncome={user?.user?.occupation?.annual_income}
                networth={user?.user?.occupation?.net_worth}
                occupation={user?.user?.occupation?.occupation}
                lifestyle={user?.user?.occupation?.lifestyle_budget}
                datingBudget={user?.user?.relationship?.dating_budget}
                education={user?.user?.occupation?.education}
                relationship={user?.user?.relationship?.relationship_status}
                smoking={user?.user?.habit?.smoking}
                drinking={user?.user?.habit?.drinking}
                alcoholPreference={user?.user?.habit?.alcohol}
                height={user?.user?.appearance?.height}
                bodyType={user?.user?.appearance?.body_type}
                ethnicity={user?.user?.appearance?.ethnicity}
                eyeColor={user?.user?.appearance?.eye_color}
                hairColor={user?.user?.appearance?.hair_color}
                children={user?.user?.relationship?.children}
                // About Me
                personalityTraits={user?.user?.personality?.personality_traits}
                personalityType={user?.user?.personality?.personality_type}
                starSign={user?.user?.personality?.star_sign}
                dominantOrSubmissive={
                  user?.user?.personality?.dominant_or_submissive
                }
                fitnessLevel={user?.user?.habit?.fitness_level}
                hobbies={user?.user?.habit?.hobbies}
                // User Utility
                profileStatus={getProfileStatus(user?.user)}
                privatePhotoAbleToRequest={
                  user?.user?.interaction?.private_photo_requested === false
                    ? true
                    : false
                }
                privatePhotoRequested={
                  user?.user?.interaction?.private_photo_requested
                }
                privatePhotoApproval={
                  user?.user?.interaction?.private_photo_approval
                }
                requestedMyPrivatePhoto={
                  user?.user?.interaction?.requested_my_private_photo
                }
                approvedMyPrivatePhoto={
                  user?.user?.interaction?.approved_my_private_photo
                }
                isOnline={user?.user?.activity?.is_online}
                isBlocked={user?.user?.interaction?.is_blocked}
                isSilentInteraction={
                  user?.user?.interaction?.should_silent_interactions
                }
                isFavorited={user?.user?.interaction?.is_favorited}
                isCallEnabled={
                  user?.user?.private_call_settings?.enable_calls &&
                  user?.user?.private_call_settings
                    ?.system_private_calls_enabled
                }
                isSugarbaby={isDaddy}
                role={user?.user?.role}
                interest={user?.user?.interest}
                isLgbtqInterested={
                  user?.user?.relationship?.is_lgbtq_interested
                }
                // Livestream
                livestreamData={user?.user?.live_stream}
                channelId={user?.user?.live_stream?.channel_id}
                livestreamerId={user?.user?.id}
                followerCount={user?.user?.live_streaming_meta?.follower_count}
                viewerCount={user?.user?.live_stream?.viewer_count}
                diamondCount={
                  user?.user?.live_streaming_meta?.lifetime_diamonds_earned
                }
                isFollowing={user?.user?.live_streaming_meta?.is_following}
                // Card Utility
                cardStatus={userCardConst.cardStatus.default}
                // Gaming View
                backgroundImage={user?.logo}
              />

              {selectedView === searchConst.userCardDisplay.list && <Divider />}
            </div>
          ))}
        </div>
      );
    } else if (
      firstLoad &&
      (getDatingMasqueradePartyFetching || getDatingMasqueradePartyLoading)
    ) {
      masqueradePartyListView = (
        <SkeletonUserCardList numberOfCards={4} selectedView={selectedView} />
      );
    } else {
      masqueradePartyListView = <EmptyList />;
    }

    return (
      <div
        id="search-card-container-masquerade-party-view-subcomponent"
        className={masqueradePartyLists?.length < 0 ? "full-height" : ""}
      >
        <PullToRefresh
          className="pull-to-refresh-container"
          onRefresh={pullToRefreshFunc}
          refreshingContent={t("common.release")}
          pullingContent={t("common.pull_to_refresh")}
          isPullable={
            !getDatingMasqueradePartyFetching &&
            !getDatingMasqueradePartyLoading
          }
        >
          <div className="masquerade-party-list-container">
            <InfiniteScroll
              pageStart={0}
              loadMore={infiniteScrollFunc}
              hasMore={masqueradePartyPagination ? true : false}
              loader={<Spinner key={0} />}
              useWindow={false}
              getScrollParent={() => document.getElementById("search-page")}
              threshold={isMobile ? 10 : 50}
            >
              {masqueradePartyListView}

              {masqueradePartyLists?.length > 0 &&
                !masqueradePartyPagination && <ResultEndCard />}
            </InfiniteScroll>
          </div>
        </PullToRefresh>
      </div>
    );
  }
};

export default MasqueradePartyView;
